import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  VaButton,
  VaTextInput,
} from '@department-of-veterans-affairs/web-components/react-bindings';
import useGeoLocation from '../../hooks/useGeoLocation';

/**
 * Search input with a submit button and optional "Use my location".
 * Owns the draft text. Trims and reports every submission (possibly empty).
 * @param {object} props
 * @param {string} props.searchLabel
 * @param {(query: string) => void} props.onSubmit Receives the trimmed query
 * @param {string} [props.defaultQuery] Initial draft text
 * @param {string} [props.error] Empty string hides the error
 * @param {(location: { coordinates: number[], placeName: string } | null) => void} [props.onGeoLocate]
 *   Shows "Use my location" when passed. Called once per lookup. On success, the input is
 *   filled with `placeName` first; `coordinates` are [longitude, latitude]. On failure,
 *   receives `null` and the input is unchanged; the parent shows any error through `error`.
 */
export function SearchControls({
  searchLabel,
  onSubmit,
  defaultQuery = '',
  error = '',
  onGeoLocate,
}) {
  const { locate, inProgress: geolocationInProgress } = useGeoLocation();
  const [query, setQuery] = useState(defaultQuery);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSubmit = () => onSubmit(query.trim());

  const handleGeoLocate = async () => {
    if (geolocationInProgress) return;
    const location = await locate();
    // The parent may be gone too, so don't report after unmount
    if (!isMounted.current) return;
    if (location) setQuery(location.placeName);
    onGeoLocate(location);
  };

  return (
    <div id="facility-search-controls">
      <div id="facility-search-input">
        <VaTextInput
          label={searchLabel}
          name="facility-search-query"
          required
          value={query}
          onInput={e => setQuery(e.target.value)}
          onKeyDown={e => {
            // Submit only for Enter in the input.
            // Enter on the "Use my location" button should activate the button instead.
            if (e.key === 'Enter' && e.target === e.currentTarget) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          {...{ error }}
        >
          {!!onGeoLocate && (
            // eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component
            <button
              type="button"
              id="use-my-location-link"
              aria-disabled={geolocationInProgress}
              onClick={handleGeoLocate}
            >
              <va-icon icon="near_me" size={3} />
              <span role="status">
                {geolocationInProgress
                  ? 'Fetching your location...'
                  : 'Use my location'}
              </span>
            </button>
          )}
        </VaTextInput>
      </div>
      <div className="vads-u-margin-right--neg1">
        <VaButton
          id="facility-search-btn"
          onClick={handleSubmit}
          text="Search"
        />
      </div>
    </div>
  );
}

SearchControls.propTypes = {
  searchLabel: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultQuery: PropTypes.string,
  error: PropTypes.string,
  onGeoLocate: PropTypes.func,
};
